import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  GroupHeader,
  GroupItems,
} from '../../../../shared/autocomplete-groups-ui';
import { actionOptions } from '../action-options';
import { RulesEngineFormValues } from '../rules-engine-form-page-old';
import ChangeCalendarTimezoneAction from '../rule-actions/change-calendar-timezone-action';
import RunPolicyAction from '../rule-actions/run-policy-action';
import SendChatAction from '../rule-actions/send-chat-action';
import { Policy } from '../../../../../types';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import SendEmailAction from '../rule-actions/send-email-action';
import LimitOccurrencesAction from '../rule-actions/limit-occurrences-action';
import { FormSubSteps } from '../../../../shared/shared-form-stepper';
import SharedActionAccordion from '../../../../shared/shared-action-accordion';
import AddToGroupAction from '../rule-actions/add-to-group';
import RemoveFromGroupAction from '../rule-actions/remove-from-group';
import ApplyGroupTemplateAction from '../rule-actions/apply-group-template-action';
import SendEmailTemplateAction from '../rule-actions/send-email-template-action';
import { checkActionAvailability } from '../steps/actions-step.utils';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AddToDrive from '@mui/icons-material/AddToDrive';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import UnfoldLess from '@mui/icons-material/UnfoldLess';

interface ActionsStepProps {
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  allPolicies: Policy[];
  expandedIds: Set<string>;
  setExpandedIds: Dispatch<SetStateAction<Set<string>>>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  setActionSubSteps: Dispatch<SetStateAction<FormSubSteps[]>>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
  toolsRoles: string[];
  onCreatePolicy: () => void;
  onCreateEmailTemplate: () => void;
  isSavingDraft: boolean;
  autoSelectEmailTemplateId?: number;
  emailSubjectFromState?: string;
  emailMessageFromState?: string;
}

export default function ActionsStep({
  control,
  errors,
  watch,
  trigger,
  allPolicies,
  setValue,
  expandedIds,
  setExpandedIds,
  setActionSubSteps,
  clearErrors,
  toolsRoles,
  onCreatePolicy,
  onCreateEmailTemplate,
  isSavingDraft,
  autoSelectEmailTemplateId,
  emailSubjectFromState,
  emailMessageFromState,
}: ActionsStepProps) {
  const theme = useTheme();
  const canCreatePolicy = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CreatePolicy.value
  );
  const actions = watch('actionInfo.actionTodo');
  const triggerEvent = watch('triggerInfo.triggerEvent');
  const triggerGroup = watch('triggerInfo.triggerGroup');

  const isActionAvailable = (actionValue: string) => {
    return checkActionAvailability(actionValue, triggerGroup, triggerEvent);
  };

  const processedActionOptions = useMemo(() => {
    const actorActionsForDrive = [
      'addToGroup',
      'removeFromGroup',
      'resetPassword',
      'suspendUser',
    ];
    return actionOptions.map((option) => {
      if (
        triggerGroup === 'drive' &&
        actorActionsForDrive.includes(option.value)
      ) {
        return {
          ...option,
          type: 'Actor',
        };
      }
      return option;
    });
  }, [triggerGroup, actionOptions]);

  const handleExpandAll = () => {
    setExpandedIds(new Set(actions));
  };

  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };

  const toggleExpand = (id: string, isExpanded: boolean) => {
    setExpandedIds((prev) => {
      const copy = new Set(prev);
      if (isExpanded) {
        copy.add(id);
      } else {
        copy.delete(id);
      }
      return copy;
    });
  };

  const removeAction = (action: string) => {
    const current = watch('actionInfo.actionTodo');
    setValue(
      'actionInfo.actionTodo',
      current.filter((a: string) => a !== action)
    );

    setExpandedIds((prev) => {
      const copy = new Set(prev);
      copy.delete(action);
      return copy;
    });

    setActionSubSteps((prev) =>
      prev.filter(
        (value) =>
          value.title !==
          processedActionOptions.find((option) => option.value === action)!
            .displayValue
      )
    );

    switch (action) {
      case 'limitOccurrences':
        setValue('actionInfo.occurrenceLimit', 0);
        clearErrors('actionInfo.occurrenceLimit');
        break;
      case 'runPolicy':
        setValue('policyInfo.policyId', 0);
        setValue('policyInfo.policyType', '');
        clearErrors('policyInfo');
        break;
      case 'sendEmail':
        setValue('emailInfo.emailNotificationEmails', []);
        setValue('emailInfo.emailSubject', '');
        setValue('emailInfo.emailMessage', '');
        clearErrors('emailInfo');
        break;
      case 'sendEmailTemplate':
        setValue('emailInfo.emailNotificationEmails', []);
        setValue('emailInfo.ccEmails', []);
        setValue('emailInfo.bccEmails', []);
        setValue('emailInfo.emailSubject', '');
        setValue('emailInfo.emailMessage', '');
        clearErrors('emailInfo');
        break;
      case 'sendChatMessage':
        setValue('chatInfo.chatUrl', '');
        setValue('chatInfo.chatMessage', '');
        clearErrors('chatInfo');
        break;
      case 'addToGroup':
        setValue('groupInfo.groupEmail', []);
        setValue('groupInfo.groupRole', []);
        clearErrors('groupInfo');
        break;
      case 'removeFromGroup':
        setValue('removeGroupInfo.groupEmail', []);
        clearErrors('removeGroupInfo');
        break;
      case 'applyGroupTemplate':
        setValue('groupSettingsInfo.groupTemplateId', 0);
        clearErrors('groupSettingsInfo.groupTemplateId');
        break;
      default:
        break;
    }
  };

  const iconStyle = {
    width: 20,
    height: 20,
    color: theme.palette.primary.main,
  };

  const getActionIcon = (actionType: string): React.ReactNode => {
    switch (actionType) {
      case 'External':
        return <EmailOutlined sx={iconStyle} />;
      case 'Policy':
        return <DescriptionOutlined sx={iconStyle} />;
      case 'User':
        return <PersonOutlined sx={iconStyle} />;
      case 'Actor':
        return <PersonOutlined sx={iconStyle} />;
      case 'Resource Calendar':
        return <CalendarTodayOutlined sx={iconStyle} />;
      case 'Drive':
        return <AddToDrive sx={iconStyle} />;
      default:
        return <PersonOutlined sx={iconStyle} />;
    }
  };
  function hasConfigurationError(action: string) {
    switch (action) {
      case 'runPolicy':
        return !watch('policyInfo.policyId');
      case 'sendEmail':
        return watch('emailInfo.emailNotificationEmails').length === 0;
      case 'sendEmailTemplate':
        return watch('emailInfo.emailNotificationEmails').length === 0;
      case 'addToGroup':
        return watch('groupInfo.groupEmail')?.length === 0;
      case 'removeFromGroup':
        return watch('removeGroupInfo.groupEmail')?.length === 0;
      case 'sendChatMessage':
        return !watch('chatInfo.chatMessage') || !watch('chatInfo.chatUrl');
      case 'limitOccurrences':
        return !watch('actionInfo.occurrenceLimit');
      case 'applyGroupTemplate':
        return !watch('groupSettingsInfo.groupTemplateId');
      default:
        return false;
    }
  }

  function hasValidationError(action: string) {
    switch (action) {
      case 'runPolicy':
        return !!errors.policyInfo;
      case 'sendEmail':
        return !!errors.emailInfo;
      case 'sendEmailTemplate':
        return !!errors.emailInfo;
      case 'addToGroup':
        return !!errors.groupInfo;
      case 'removeFromGroup':
        return !!errors.removeGroupInfo;
      case 'sendChatMessage':
        return !!errors.chatInfo;
      case 'limitOccurrences':
        return !!errors.actionInfo?.occurrenceLimit;
      case 'applyGroupTemplate':
        return !!errors.groupSettingsInfo?.groupTemplateId;
      default:
        return false;
    }
  }

  useEffect(() => {
    const nextSubSteps = actions.map((x) => ({
      stepIndex: 'Actions',
      title: processedActionOptions.find((option) => option.value === x)!
        .displayValue,
      hasError: hasConfigurationError(x) || hasValidationError(x),
    }));

    setActionSubSteps((prev) => {
      const same =
        prev.length === nextSubSteps.length &&
        prev.every(
          (p, i) =>
            p.title === nextSubSteps[i].title &&
            p.hasError === nextSubSteps[i].hasError
        );

      return same ? prev : nextSubSteps;
    });
  }, [
    actions,
    processedActionOptions,
    hasConfigurationError,
    hasValidationError,
  ]);

  return (
    <Stack gap={2} width={'100%'}>
      <Typography variant="caption">
        Select the actions you would like to occur when the rule is triggered.
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Stack
          direction="row"
          gap={1}
          sx={{ width: '100%', alignItems: 'start' }}
        >
          <Controller
            rules={{
              required: 'Please add at least one action.',
            }}
            name="actionInfo.actionTodo"
            control={control}
            render={({ field }) => {
              const isRunPolicyDisabled =
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.ViewPolicies.value
                ) ||
                !isAbleToSeeUIElement(
                  toolsRoles,
                  ToolsRoles.MaunalRunPolicy.value
                );
              return (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Autocomplete
                    multiple
                    limitTags={2}
                    autoComplete
                    autoHighlight
                    options={processedActionOptions
                      .filter((action) => isActionAvailable(action.value))
                      .sort((a, b) => a.type.localeCompare(b.type))}
                    getOptionLabel={(option) => option.displayValue}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Action"
                        error={!!errors.actionInfo?.actionTodo}
                        helperText={
                          errors?.actionInfo?.actionTodo?.message || ''
                        }
                      />
                    )}
                    groupBy={(option) => option.type}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <GroupHeader>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    )}
                    filterSelectedOptions
                    fullWidth
                    getOptionDisabled={(option) =>
                      option.value === 'runPolicy' && isRunPolicyDisabled
                    }
                    slotProps={{
                      listbox: {
                        sx: {
                          '& .MuiAutocomplete-option[aria-disabled="true"]': {
                            pointerEvents: 'auto',
                          },
                        },
                      },
                    }}
                    renderOption={(props, option) => {
                      const isDisabled =
                        option.value === 'runPolicy' && isRunPolicyDisabled;
                      return (
                        <Box
                          component="li"
                          {...props}
                          key={option.value}
                          onClick={
                            isDisabled
                              ? (e: React.MouseEvent) => e.stopPropagation()
                              : (props as React.HTMLAttributes<HTMLLIElement>)
                                  .onClick
                          }
                        >
                          <Tooltip
                            arrow
                            title={
                              isDisabled
                                ? 'You do not have permission to run this Policy.'
                                : ''
                            }
                            placement="right"
                          >
                            <span style={{ display: 'block' }}>
                              {option.displayValue}
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    }}
                    value={field.value.map(
                      (value) =>
                        processedActionOptions.find(
                          (option) => option.value === value
                        )!
                    )}
                    onChange={async (_, data) => {
                      const newValues = data.map((x) => x.value);
                      const addedValues = newValues.filter(
                        (v) => !field.value.includes(v)
                      );

                      field.onChange(newValues);
                      if (newValues.length > 0) {
                        clearErrors('actionInfo.actionTodo');
                      }

                      if (addedValues.length > 0) {
                        if (hasConfigurationError(addedValues[0])) {
                          setExpandedIds(new Set([addedValues[0]]));
                        }
                      }

                      setActionSubSteps(
                        data.map((x) => {
                          return {
                            stepIndex: 'Actions',
                            title: processedActionOptions.find(
                              (option) => option.value === x.value
                            )!.displayValue,
                            hasError: hasConfigurationError(x.value),
                          };
                        })
                      );
                    }}
                  />
                </Box>
              );
            }}
          />
          <Tooltip arrow title="Collapse/Expand All">
            <IconButton
              sx={{ mt: 0.5 }}
              onClick={() =>
                expandedIds.size === actions.length
                  ? handleCollapseAll()
                  : handleExpandAll()
              }
              size="small"
            >
              <UnfoldLess />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          width: '100%',
          p: 0.5,
        }}
      >
        <Stack
          gap={1}
          flexDirection="column"
          sx={{
            width: '100%',
            overflowX: 'visible',
          }}
        >
          {[...actions].reverse().map((action) => {
            const setAction = processedActionOptions.find(
              (option) => option.value === action
            )!;
            let content: React.ReactNode = null;

            switch (action) {
              case 'sendChatMessage':
                content = (
                  <SendChatAction
                    control={control}
                    errors={errors}
                    clearErrors={clearErrors}
                    watch={watch}
                  />
                );
                break;
              case 'changeCalendarTimezone':
                content = (
                  <ChangeCalendarTimezoneAction
                    key={action}
                    control={control}
                  />
                );
                break;
              case 'runPolicy':
                content = (
                  <RunPolicyAction
                    key={action}
                    control={control}
                    errors={errors}
                    allPolicies={allPolicies}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    onCreatePolicy={onCreatePolicy}
                    canCreatePolicy={canCreatePolicy}
                  />
                );
                break;
              case 'addToGroup':
                content = (
                  <AddToGroupAction
                    key={action}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                  />
                );
                break;
              case 'removeFromGroup':
                content = (
                  <RemoveFromGroupAction
                    key={action}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                  />
                );
                break;
              case 'sendEmail':
                content = (
                  <SendEmailAction
                    key={action}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    trigger={trigger}
                    clearErrors={clearErrors}
                  />
                );
                break;
              case 'sendEmailTemplate':
                content = (
                  <SendEmailTemplateAction
                    key={action}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    trigger={trigger}
                    clearErrors={clearErrors}
                    onCreateEmailTemplate={onCreateEmailTemplate}
                    isSavingDraft={isSavingDraft}
                    autoSelectEmailTemplateId={autoSelectEmailTemplateId}
                    emailSubjectFromState={emailSubjectFromState}
                    emailMessageFromState={emailMessageFromState}
                  />
                );
                break;
              case 'limitOccurrences':
                content = (
                  <LimitOccurrencesAction
                    key={action}
                    control={control}
                    errors={errors}
                    watch={watch}
                  />
                );
                break;
              case 'applyGroupTemplate':
                content = (
                  <ApplyGroupTemplateAction control={control} errors={errors} />
                );
                break;
              default:
                return null;
            }

            return (
              <SharedActionAccordion
                key={action}
                id={action}
                icon={getActionIcon(setAction.type)}
                title={setAction?.displayValue}
                description={setAction.description}
                expanded={expandedIds.has(action)}
                onToggleExpand={(exp) => toggleExpand(action, exp)}
                onRemove={() => removeAction(action)}
                hasNeedsAttention={
                  hasConfigurationError(action) || hasValidationError(action)
                }
              >
                {content}
              </SharedActionAccordion>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
